import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useMemo } from 'react';
import { styled, Paper, Grid, Box } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ItemPhotoGroup from '../../molecules/ItemPhotoGroup';
import { H6, Label2 } from '../../atoms/typography';
import { Alert } from '../../atoms/Alert';
import ItemAlertBorder from '../../atoms/ItemAlertBorder';
import ShortcutKey from '../../atoms/ShortcutKey';
import { hexToRgb } from '../../../utils';
const hotKeyDisabled = `
  background: linear-gradient(to bottom right, rgba(0, 0, 0, 0.12) calc(50% - 2px), #A5A6A7 , rgba(0, 0, 0, 0.12) calc(50% + 2px) )
`;
const HotKeyContainer = styled(Box, {
    shouldForwardProp: (prop) => {
        return !['isSelected', 'isDisabled', 'customColor'].includes(prop);
    },
}) `
  position: relative;
  height: 42px;
  min-height: 42px;
  width: 42px;
  min-width: 42px;
  transition: all 0.0s;
  border-radius: 4px;
  border: 2px solid;
  border-color: ${({ theme, isSelected = false, isDisabled, customColor, }) => {
    if (isSelected)
        return theme.palette.primary.main;
    if (isDisabled)
        return '#A5A6A7';
    return customColor ? `rgba(${hexToRgb(customColor)}, 0.5)` : theme.palette.grey[400];
}};
  background-color: ${({ theme, isSelected = false }) => isSelected ? theme.palette.primary.main : 'transparent'};
  color: ${({ theme, isSelected = false, isDisabled, customColor, }) => {
    if (isSelected)
        return theme.palette.primary.contrastText;
    if (isDisabled)
        return '#A5A6A7';
    return customColor ?? theme.palette.text.primary;
}};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ isDisabled }) => isDisabled && hotKeyDisabled};
  z-index: 2;
  span.shortcut-key-span {
    font-size: 1rem !important;
    font-weight: 500;
    z-index: 1;
    ${({ isDisabled }) => { if (isDisabled)
    return `background-color: transparent;`; }};
    ${({ isSelected, theme }) => { if (isSelected)
    return `background-color: ${theme.palette.primary.main};`; }};
  }
`;
const SelectedIndicator = styled(HotKeyContainer) `
  border-radius: 50%;
`;
const StyledPaper = styled(Paper, {
    shouldForwardProp: (prop) => {
        return !['isSelected', 'isDisabled', 'hidePhotoCard'].includes(prop);
    }
}) `
  cursor: pointer;
  border: 4px ${({ isSelected }) => isSelected ? 'solid' : 'none'} ${({ theme }) => theme.palette.primary.main};
  background: ${({ theme, isDisabled }) => isDisabled ? theme.palette.action.disabledBackground : 'none'};
  padding: ${({ isSelected }) => isSelected ? '0' : '5px'};
  height: fit-content;
  position: relative;
  ${({ hidePhotoCard }) => hidePhotoCard &&
    `.question-card__images { display: none; }
  `}
  ${({ isSelected }) => isSelected && `
    & .alert-border { border: none; }
  `}
  ${({ isDisabled }) => isDisabled && `
    cursor: default;
    .zoom-icon { display: none; }
    & .item-photo-container {
      position: relative;
      :before { display: none; }
      :after {
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        content: '';
        position: absolute;
        background-color: rgba(255, 255, 255, 0.4);
        z-index: 3;
      }
      .item-photo {
        filter: grayscale(1);
      }
    }
  `}
`;
const QuestionCard = ({ question, description, hotKey, hotKeyColor, isDisabled = false, imageUrls = [], isSelectable = false, isSelected: externalSelection = false, selectedText, selectedSubText, onClick, hideUnavailablePhoto = true, ...muiProps }) => {
    const [isSelected, setIsSelected] = useState(externalSelection);
    const updateSelection = (selected) => {
        if (selected !== isSelected) {
            setIsSelected(selected);
        }
    };
    useEffect(() => {
        updateSelection(externalSelection);
    }, [externalSelection]);
    const hidePhotoCard = useMemo(() => {
        return (imageUrls?.length === 0) && hideUnavailablePhoto;
    }, [imageUrls, hideUnavailablePhoto]);
    const handleClick = () => {
        if (isSelectable) {
            setIsSelected(!isSelected);
            onClick?.(!isSelected);
        }
    };
    const hotkey = _jsx(Grid, { item: true, children: hotKey ? (_jsx(HotKeyContainer, { isSelected: isSelected, isDisabled: isDisabled, customColor: hotKeyColor, "data-testid": "hotkey", children: _jsx(ShortcutKey, { color: hotKeyColor ?? 'text.secondary', onPress: handleClick, keyToShow: hotKey?.[0], keysToPress: hotKey?.[0], disabled: isDisabled, variant: 'contained' }) })) : (_jsx(SelectedIndicator, { isSelected: isSelected })) });
    return (_jsxs(StyledPaper, { "data-testid": "question-card", onClick: handleClick, isSelected: isSelected, isDisabled: isDisabled, hidePhotoCard: hidePhotoCard, className: "question-card", ...muiProps, children: [_jsxs(Grid, { className: "question-card__contents", container: true, spacing: 1, direction: 'column', padding: 1, children: [_jsxs(Grid, { className: "question-card__header", item: true, container: true, spacing: 1, alignItems: 'center', wrap: "nowrap", children: [hotkey, _jsxs(Grid, { item: true, children: [_jsx(H6, { color: isDisabled ?
                                            'text.disabled' :
                                            hotKeyColor ?? 'text.primary', children: question }), _jsx(Label2, { color: isDisabled ? 'text.disabled' : 'text.secondary', children: description })] })] }), _jsx(Grid, { className: "question-card__images", container: true, pl: 1, pt: 1, height: "100%", children: _jsx(ItemPhotoGroup, { imageUrls: imageUrls, showWhenNoPhotoAvailable: !hideUnavailablePhoto }) })] }), isSelected && (selectedText || selectedSubText) &&
                _jsx(Grid, { item: true, container: true, direction: 'row', bgcolor: isDisabled ? 'text.disabled' : 'primary.main', color: theme => theme.palette.info.contrastText, children: _jsx(Alert, { icon: _jsx(ArrowForwardIcon, {}), severity: 'primary', variant: 'filled', title: selectedText, children: selectedSubText }) }), isSelected && (_jsx(ItemAlertBorder, { variant: 'check' }))] }));
};
export default QuestionCard;
//# sourceMappingURL=index.js.map